import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { clearAuthData, isAuthenticated } from '../../utils/tokenManager';
import { useTheme } from '../../contexts/ThemeContext';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useLanguage } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { useLogoutMutation } from '../../store/api/authApi';
import { toast } from 'sonner';

// Simple SVG Icons
const MenuIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const SunIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

interface HeaderProps {
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
}

export function Header({ onMenuToggle, isMenuOpen, menuButtonRef }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const auth = isAuthenticated();
  const isHomePage = location.pathname === '/';
  const showMobileNav = !auth;
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile] = useUpdateProfileMutation();

  const toggleLanguage = () => {
    void (async () => {
      const previous = currentLanguage;
      const next: 'ar' | 'en' = previous === 'ar' ? 'en' : 'ar';
      changeLanguage(next);
      if (!auth) return;
      try {
        await updateProfile({ language: next }).unwrap();
      } catch {
        changeLanguage(previous);
        toast.error(t('navigation.languageSaveError'));
      }
    })();
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [logout] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Always clear local state even if the API call fails
    }
    clearAuthData();
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const user = auth
    ? (() => {
        try {
          const stored = localStorage.getItem('user');
          return stored
            ? (JSON.parse(stored) as { firstName?: string; email?: string })
            : null;
        } catch {
          return null;
        }
      })()
    : null;

  return (
    <header
      className={`fixed left-4 right-4 top-4 z-50 mx-auto w-[calc(100%-2rem)] max-w-7xl rounded-2xl shadow-2xl transition-all duration-300 header-glass ${
        isScrolled
          ? 'bg-brand-primary/45 backdrop-blur-2xl backdrop-saturate-150'
          : 'bg-brand-primary/30 backdrop-blur-xl backdrop-saturate-125'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="md:h-18 flex h-14 items-center justify-between gap-2">
          {/* Logo, Site Name, and Menu Toggle */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            {/* على الموبايل للضيف: زر القائمة (يظهر تحت lg فقط)؛ للمستخدم: زر السايدبار */}
            {(showMobileNav || (auth && onMenuToggle)) && (
              <div className={`flex flex-shrink-0 ${showMobileNav ? 'max-lg:flex lg:hidden' : ''}`}>
                <button
                  type="button"
                  ref={auth && onMenuToggle ? menuButtonRef : undefined}
                  onClick={showMobileNav ? () => setIsMobileNavOpen((o) => !o) : onMenuToggle}
                  className="rounded-lg p-2 text-neutral-light transition-colors duration-200 hover:bg-neutral-dark/50 hover:text-brand-accent"
                  aria-label={
                    showMobileNav
                      ? (isMobileNavOpen ? t('navigation.closeMenu') : t('navigation.menu'))
                      : (isMenuOpen ? t('navigation.closeMenu') : t('navigation.menu'))
                  }
                  aria-expanded={showMobileNav ? isMobileNavOpen : isMenuOpen}
                >
                  {(showMobileNav ? isMobileNavOpen : isMenuOpen) ? <CloseIcon /> : <MenuIcon />}
                </button>
              </div>
            )}
            <Link
              to={auth ? '/dashboard' : '/'}
              className="group flex min-w-0 flex-shrink items-center gap-2 transition-transform duration-300 hover:scale-105 sm:gap-3"
              aria-label="CYB Home"
            >
              <img
                src="https://res.cloudinary.com/dwhpimotj/image/upload/v1758543752/logo_pnm9g9.png"
                alt="CYB Logo"
                className="h-7 w-auto flex-shrink-0 sm:h-8 md:h-10"
              />
              {/* اسم الموقع يظهر على الموبايل بدل الأزرار الكبيرة */}
              <span className="relative flex-shrink-0 overflow-hidden rounded px-1 py-0.5 font-heading text-base font-bold text-brand-accent transition-colors duration-200 group-hover:text-brand-accent sm:text-xl md:text-2xl">
                <span className="relative z-10 drop-shadow-lg">CYB</span>
              </span>
            </Link>

            {/* على الصفحة الرئيسية بالموبايل: زر الثيم وزر اللغة جنب الشعار */}
            {isHomePage && showMobileNav && (
              <div className="ms-auto flex flex-shrink-0 items-center gap-px rounded-lg bg-neutral-light/5 p-1 ring-1 ring-neutral-light/10 lg:hidden">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-light transition-all duration-200 hover:bg-neutral-light/10 hover:text-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                  aria-label={theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                  title={theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                >
                  {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>
                <div className="h-5 w-px bg-neutral-light/10" />
                <LanguageSwitcher />
              </div>
            )}

            {/* Controls: Theme + Language (للمستخدمين ولغير الضيوف) */}
            {!showMobileNav && (
              <div className="flex flex-shrink-0 items-center">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-neutral-light transition-all duration-200 hover:bg-neutral-dark/50 hover:text-brand-accent focus:outline-none"
                  aria-label={theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                  title={theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                >
                  {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>
              </div>
            )}
          </div>

          {/* Navigation Menu - Only on Home Page */}
          {isHomePage && !auth && (
            <nav className="hidden items-center gap-6 lg:flex">
              <button
                onClick={() => scrollToSection('hero')}
                className="px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('benefits')}
                className="px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.benefits')}
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.howItWorks')}
              </button>
            </nav>
          )}

          {/* User Actions */}
          <div className={`flex flex-shrink-0 items-center gap-3 md:gap-4 ${showMobileNav ? 'max-lg:hidden' : ''}`}>
            {/* User Menu */}
            {auth ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="group flex items-center gap-2 rounded-lg bg-neutral-dark/50 px-2.5 py-1.5 text-neutral-light transition-colors duration-200 hover:bg-neutral-dark"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <UserIcon />
                  <span className="hidden font-body text-sm md:block">
                    {user?.firstName || user?.email || 'User'}
                  </span>
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute end-0 top-full mt-2 w-48 animate-slide-down overflow-hidden rounded-lg card-glass shadow-xl overflow-hidden">
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-light transition-colors duration-200 hover:bg-brand-accent/20 hover:text-brand-accent"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-body">
                        {t('navigation.profile')}
                      </span>
                    </Link>
                    <div className="mx-3 my-1 h-px bg-neutral-light/10" />
                    <button
                      type="button"
                      onClick={() => { toggleLanguage(); setIsUserMenuOpen(false); }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-start text-neutral-light transition-colors duration-200 hover:bg-brand-accent/20 hover:text-brand-accent"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-body flex items-center gap-2">
                        {t('navigation.language')}
                        <span className="rounded bg-neutral-light/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider">
                          {currentLanguage === 'ar' ? 'EN' : 'AR'}
                        </span>
                      </span>
                    </button>
                    <div className="mx-3 my-1 h-px bg-neutral-light/10" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-3 text-start text-neutral-light transition-colors duration-200 hover:bg-danger/20 hover:text-danger"
                    >
                      <LogoutIcon />
                      <span className="font-body">
                        {t('navigation.logout')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="rounded-lg px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
                >
                  {t('auth.login.title')}
                </Link>
                <Link
                  to="/register"
                  className="rounded-lg bg-brand-accent px-3 py-1.5 font-heading font-semibold text-brand-primary transition-colors duration-200 hover:bg-brand-accent/90"
                >
                  {t('auth.register.title')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* القائمة الجانبية للموبايل: عبر Portal في body + خلفية صلبة غير شفافة */}
      {showMobileNav &&
        isMobileNavOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100] bg-black/50 lg:hidden"
              onClick={() => setIsMobileNavOpen(false)}
              aria-hidden="true"
            />
            <div
              className="fixed top-0 start-0 z-[101] flex h-full w-72 max-w-[85vw] flex-col border-e border-neutral-dark/50 shadow-2xl lg:hidden"
              role="dialog"
              aria-label={t('navigation.menu')}
              style={{
                backgroundColor: theme === 'dark' ? '#0E4A4A' : '#ffffff',
                minHeight: '100vh',
              }}
            >
              <div className={`flex flex-col gap-4 p-4 ${theme === 'light' ? 'text-neutral-dark' : ''}`}>
                <div className="flex items-center justify-between border-b border-neutral-dark/50 pb-3">
                  <span className="font-heading text-lg font-bold text-brand-accent">CYB</span>
                  <button
                    type="button"
                    onClick={() => setIsMobileNavOpen(false)}
                    className={`rounded-lg p-2 ${theme === 'light' ? 'text-neutral-dark hover:bg-neutral-dark/20' : 'text-neutral-light hover:bg-neutral-dark/50'}`}
                    aria-label={t('navigation.closeMenu')}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <Link
                  to="/login"
                  onClick={() => setIsMobileNavOpen(false)}
                  className={`rounded-lg px-4 py-3 font-body transition-colors ${theme === 'light' ? 'text-neutral-dark hover:bg-neutral-dark/20 hover:text-brand-accent' : 'text-neutral-light hover:bg-neutral-dark/50 hover:text-brand-accent'}`}
                >
                  {t('auth.login.title')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="rounded-lg bg-brand-accent px-4 py-3 font-heading font-semibold text-brand-primary text-center transition-colors hover:bg-brand-accent/90"
                >
                  {t('auth.register.title')}
                </Link>
                <div className={`mt-2 flex items-center justify-between rounded-lg border px-4 py-3 ${theme === 'light' ? 'border-neutral-dark/30 text-neutral-dark' : 'border-neutral-dark/50'}`}>
                  <span className="font-body text-sm text-neutral-medium">{t('navigation.language')}</span>
                  <LanguageSwitcher />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    toggleTheme();
                  }}
                  className={`flex items-center justify-between rounded-lg px-4 py-3 font-body text-sm transition-colors ${theme === 'light' ? 'text-neutral-dark hover:bg-neutral-dark/20 hover:text-brand-accent' : 'text-neutral-light hover:bg-neutral-dark/50 hover:text-brand-accent'}`}
                >
                  <span>
                    {theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                  </span>
                  {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Click outside to close user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}
